import type { CashDenominationRowDto } from './cash-denominations.dto.js';
import type { CashOperationIdentityDto } from './cash-operation-identity.dto.js';

export const cashVaultRemittanceStatusValues = [
  'draft',
  'prepared',
  'awaiting_destination_confirmation',
  'confirmed',
  'cancelled',
  'returned',
  'exception',
  'voided',
] as const;
export type CashVaultRemittanceStatus = (typeof cashVaultRemittanceStatusValues)[number];

export const cashVaultRemittanceDestinationTypeValues = [
  'central_treasury',
  'bank_deposit',
  'external_physical',
] as const;
export type CashVaultRemittanceDestinationType =
  (typeof cashVaultRemittanceDestinationTypeValues)[number];

export const cashVaultRemittanceEventTypeValues = [
  'created',
  'updated',
  'prepared',
  'handed_off',
  'confirmed',
  'cancelled',
  'returned',
  'excepted',
  'voided',
] as const;
export type CashVaultRemittanceEventType = (typeof cashVaultRemittanceEventTypeValues)[number];

export const cashAvailabilityReservationStatusValues = ['active', 'released', 'consumed'] as const;
export type CashAvailabilityReservationStatus =
  (typeof cashAvailabilityReservationStatusValues)[number];

export interface CashRemittanceDestinationDto {
  id: string;
  companyId: string;
  type: CashVaultRemittanceDestinationType;
  code: string;
  displayName: string;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListCashRemittanceDestinationsRequest {
  companyId: string;
  activeOnly?: boolean;
}

export interface ListCashRemittanceDestinationsResponse {
  items: CashRemittanceDestinationDto[];
}

export interface UpsertCashRemittanceDestinationRequest {
  companyId: string;
  operationId: string;
  destinationId?: string;
  type: CashVaultRemittanceDestinationType;
  code: string;
  displayName: string;
  isActive: boolean;
  metadata?: Record<string, unknown> | null;
  occurredAt?: string;
}

export interface CashVaultRemittanceDto extends CashOperationIdentityDto {
  id: string;
  status: CashVaultRemittanceStatus;
  storeId: string;
  locationId: string;
  stationId: string;
  sourceVaultCashLocationId: string;
  destinationType: CashVaultRemittanceDestinationType;
  destinationId: string;
  destinationCashLocationIdSnapshot: string | null;
  amountMinor: number;
  denominations: CashDenominationRowDto[] | null;
  reference: string | null;
  note: string | null;
  preparedByUserId: string | null;
  preparedAt: string | null;
  handedOffByUserId: string | null;
  handedOffAt: string | null;
  vaultOutMovementId: string | null;
  destinationMovementId: string | null;
  returnedVaultInMovementId: string | null;
  resolutionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCashVaultRemittanceRequest {
  companyId: string;
  operationId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  sourceVaultCashLocationId: string;
  destinationType: CashVaultRemittanceDestinationType;
  destinationId: string;
  amountMinor: number;
  denominations?: CashDenominationRowDto[] | null;
  reference?: string | null;
  note?: string | null;
  occurredAt?: string;
}

export interface UpdateCashVaultRemittanceRequest {
  companyId: string;
  operationId: string;
  remittanceId: string;
  destinationType: CashVaultRemittanceDestinationType;
  destinationId: string;
  amountMinor: number;
  denominations?: CashDenominationRowDto[] | null;
  reference?: string | null;
  note?: string | null;
  occurredAt?: string;
}

export interface CashVaultRemittanceActionRequest {
  companyId: string;
  operationId: string;
  remittanceId: string;
  reason?: string | null;
  evidenceReference?: string | null;
  actualAmountMinor?: number;
  actualDenominations?: CashDenominationRowDto[] | null;
  occurredAt?: string;
}

export interface ListCashVaultRemittancesRequest {
  companyId: string;
  locationId?: string;
  sourceVaultCashLocationId?: string;
  status?: CashVaultRemittanceStatus;
}

export interface ListCashVaultRemittancesResponse {
  items: CashVaultRemittanceDto[];
}

export interface GetCashVaultRemittanceRequest {
  companyId: string;
  remittanceId: string;
  locationId?: string;
}
